/**
 * Register page — handles both new user signup + league creation and
 * authenticated user league creation.
 *
 * Routes:
 *   /register            — New league creation flow
 *   /register?invite=CODE — Existing invite-only signup (preserved)
 *   /register/success     — Post-payment success (invites step)
 */

import { useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { SocratesLogo } from '@/components/SocratesLogo'
import { StepAccountCreate } from '@/components/register/StepAccountCreate'
import { StepLeagueDetails } from '@/components/register/StepLeagueDetails'
import { StepCompetitionSelect } from '@/components/register/StepCompetitionSelect'
import { StepPayment } from '@/components/register/StepPayment'
import { StepInviteMembers } from '@/components/register/StepInviteMembers'
import { useRegisterLeague, type RegistrationStep } from '@/components/register/useRegisterLeague'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'

// Preserve the existing invite registration flow
import OldRegister from '@/pages/auth/Register'

const STEP_LABELS: { key: RegistrationStep; label: string }[] = [
  { key: 'details', label: 'League' },
  { key: 'competition', label: 'Competition' },
  { key: 'invite', label: 'Invite' },
]

function StepIndicator({ currentStep, isSuperAdmin }: { currentStep: RegistrationStep; isSuperAdmin: boolean }) {
  const steps = STEP_LABELS

  const currentIndex = steps.findIndex(s => s.key === currentStep)

  return (
    <div className="flex items-center justify-center gap-1 mb-8
                    sm:gap-2">
      {steps.map((step, index) => {
        const isComplete = currentIndex > index || currentStep === 'complete'
        const isCurrent = step.key === currentStep

        return (
          <div key={step.key} className="flex items-center">
            {index > 0 && (
              <div className={`w-6 h-px mx-1 sm:w-8 sm:mx-2 ${isComplete ? 'bg-emerald-400' : 'bg-zinc-300 dark:bg-zinc-600'}`} />
            )}
            <div className="flex items-center gap-1.5">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium shrink-0
                  ${isComplete ? 'bg-emerald-500 text-white' : ''}
                  ${isCurrent ? 'bg-zinc-800 dark:bg-zinc-200 text-white dark:text-zinc-800' : ''}
                  ${!isComplete && !isCurrent ? 'bg-zinc-200 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400' : ''}
                `}
              >
                {isComplete ? <Check className="w-3.5 h-3.5" /> : index + 1}
              </div>
              <span className={`hidden text-xs sm:inline ${isCurrent ? 'font-medium text-zinc-800 dark:text-zinc-200' : 'text-zinc-500 dark:text-zinc-400'}`}>
                {step.label}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default function Register() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()

  // If ?invite=CODE is present, render the original invite registration flow
  const inviteCode = searchParams.get('invite')
  if (inviteCode) {
    return <OldRegister />
  }

  // If still loading auth, show a brief loading state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 dark:from-zinc-950 dark:to-zinc-900 flex items-center justify-center">
        <div className="animate-pulse text-zinc-400">Loading...</div>
      </div>
    )
  }

  return <RegisterFlow />
}

function RegisterFlow() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const {
    step,
    setStep,
    formData,
    updateFormData,
    leagueId,
    setLeagueId,
    loading,
    error,
    setError,
    isSuperAdmin,
    user,
    onAccountCreated,
    createLeague,
    sendInvites,
    finalize,
    restoreState,
    clearStoredState,
  } = useRegisterLeague()

  const cancelled = searchParams.get('cancelled') === 'true'

  // When user becomes authenticated (after signup or login), advance past account step.
  // onAccountCreated handles restoring leagueId/formData from URL/localStorage for Stripe returns.
  useEffect(() => {
    if (user && step === 'account') {
      onAccountCreated()
    }
  }, [user, step, onAccountCreated])

  // Handle return from Stripe (/register/success or /register?cancelled=true).
  // Only run when user is authenticated — if session expired during checkout,
  // the re-auth effect above handles restoring state after login.
  useEffect(() => {
    if (!user) return

    const sessionId = searchParams.get('session_id')
    const returnedLeagueId = searchParams.get('league_id')

    if (cancelled) {
      const stored = restoreState()
      const resolvedLeagueId = returnedLeagueId || stored?.leagueId
      if (resolvedLeagueId) {
        setLeagueId(resolvedLeagueId)
      }
      setError(null)
      setStep('payment')
    } else if (sessionId || returnedLeagueId) {
      const stored = restoreState()
      const resolvedLeagueId = returnedLeagueId || stored?.leagueId

      if (resolvedLeagueId) {
        setLeagueId(resolvedLeagueId)
        setError(null)
        setStep('invite')
      } else {
        // Can't resolve league — localStorage was likely cleared.
        // Show error and send back to the start of the flow.
        setError('Could not restore your league. Please create a new league.')
        setStep('details')
      }
    }
  }, []) // Run once on mount

  const handleSkipInvites = async () => {
    await finalize()
    navigate('/dashboard')
  }

  const handleSendInvites = async (emails: string[]) => {
    await sendInvites(emails)
  }

  const handleComplete = async () => {
    await finalize()
    navigate('/dashboard')
  }

  // After competition step, create league immediately (payment disabled for now)
  const handleCompetitionNext = () => {
    createLeague()
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-stone-100 dark:from-zinc-950 dark:to-zinc-900 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-zinc-900 p-6 rounded-lg shadow-xl max-w-lg w-full
                      sm:p-8">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex justify-center mb-3">
            <SocratesLogo size="large" />
          </div>
          <h1 className="text-2xl font-bold text-zinc-800 dark:text-zinc-100 font-caveat">Socrates</h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">Create your prediction league</p>
        </div>

        {/* Step indicator (only show when past account creation) */}
        {step !== 'account' && step !== 'complete' && (
          <StepIndicator currentStep={step} isSuperAdmin={isSuperAdmin} />
        )}

        {/* Steps */}
        {step === 'account' && (
          <StepAccountCreate onComplete={onAccountCreated} />
        )}

        {step === 'details' && (
          <StepLeagueDetails
            formData={formData}
            onUpdate={updateFormData}
            onNext={() => setStep('competition')}
          />
        )}

        {step === 'competition' && (
          <StepCompetitionSelect
            formData={formData}
            onUpdate={updateFormData}
            onNext={handleCompetitionNext}
            onBack={() => setStep('details')}
            loading={loading}
            error={error}
          />
        )}

        {step === 'payment' && (
          <StepPayment
            formData={formData}
            loading={loading}
            error={error}
            cancelled={cancelled}
            onSubmit={createLeague}
            onBack={() => setStep('competition')}
          />
        )}

        {step === 'invite' && (
          <StepInviteMembers
            loading={loading}
            error={error}
            onSendInvites={handleSendInvites}
            onSkip={handleSkipInvites}
          />
        )}

        {step === 'complete' && (
          <div className="text-center py-6">
            <div className="flex justify-center mb-4">
              <div className="bg-emerald-100 dark:bg-emerald-900 p-3 rounded-full">
                <Check className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-zinc-800 dark:text-zinc-100 mb-2">You're all set!</h2>
            <p className="text-zinc-500 dark:text-zinc-400 text-sm mb-6">
              Your league has been created and invites have been sent.
            </p>
            <Button onClick={handleComplete} className="w-full">
              Go to your league
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
